import { useState, useEffect } from "react";
import { fetchModuleVariance } from "../../utils/dataReceiver";

/**
 * Fetch and manage state for the Completion Variance (Module Variance) modal.
 *
 * Fires once on mount. Returns an 8-week variance trend (oldest → newest)
 * plus a per-module table scoped to the same 8-week window. The graph always
 * contains exactly 8 entries - the backend pads synthetic empty weeks when
 * fewer than 8 weeks of history exist, so chart rendering is always stable.
 *
 * Cancels the in-flight request automatically if the component unmounts before
 * the response arrives (AbortController cleanup).
 *
 * Cache TTL: 5 minutes - variance data updates at most once per day but the
 * modal may be opened repeatedly within a session.
 *
 * @returns {{
 *   metrics: {
 *     eightWeekVariancePct: number,   - avg variance % across 8 weeks, 1 dp
 *                                       positive = behind schedule
 *                                       negative = ahead of schedule
 *     target:               number,   - goal from RTLS_ProductionGoals (e.g. 95)
 *     weeksAhead:           number,   - weeks where avg variance < 0
 *     weeksBehind:          number,   - weeks where avg variance > 0
 *   },
 *   graph: Array<{
 *     week:        string,   - "Wk 1" … "Wk 8" (Wk 8 = most recent)
 *     label:       string,   - "Apr 28" (Monday of that ISO week)
 *     variancePct: number,   - % slip for that week
 *   }>,
 *   table: Array<{
 *     woNum:        string,   - work order number
 *     scheduled:    string,   - "YYYY-MM-DD"
 *     actual:       string,   - "YYYY-MM-DD"
 *     varianceDays: number,   - positive = late, negative = early
 *   }>,
 *   loading: boolean,
 *   error:   string | null,
 * }}
 */

let _cache = null;
let _cacheTime = 0;
const CACHE_TTL_MS = 5 * 60_000; // 5 minutes

const METRICS_DEFAULT = {
  eightWeekVariancePct: 0,
  target: 95,
  weeksAhead: 0,
  weeksBehind: 0,
};

const useModuleVarianceData = () => {
  const [metrics, setMetrics] = useState(_cache?.metrics ?? METRICS_DEFAULT);
  const [graph, setGraph] = useState(_cache?.graph ?? []);
  const [table, setTable] = useState(_cache?.table ?? []);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const isStale = !_cache || Date.now() - _cacheTime > CACHE_TTL_MS;
    if (!isStale) return; // already fresh - skip fetch

    const load = async () => {
      try {
        const data = await fetchModuleVariance(controller.signal);
        _cache = data;
        _cacheTime = Date.now();
        setMetrics(data.metrics);
        setGraph(data.graph);
        setTable(data.table);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message ?? "Failed to load module variance data.");
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, []);

  return { metrics, graph, table, loading, error };
};

export default useModuleVarianceData;
